import React from 'react';
import type { GoodsOption } from 'shared/src/api';
import {
  Flex,
  List,
  PrimaryButton,
  Spinner,
  useBottomSheetEntered,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { PricingListItem } from './PricingListItem';
import { PricingInfo } from './PricingInfo';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { useOnPressPurchase } from '../../hooks/useOnPressPurchase';
import { useOnPressAddToCart } from '../../hooks/useOnPressAddToCart';
import { ReportPriceButton } from './ReportPriceButton';
import type { ProductOptionMaxDiscountMode } from '../../../../hooks/useProductOptionMaxDiscountMode';
import { ImageBadgeFreeDelivery } from '../../../BottomDocked/components/ImageBadgeFreeDelivery';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { useManageMaxDiscountCouponAutoRedeem } from '../../hooks/useManageMaxDiscountCouponAutoRedeem';

export const PricingListView: React.FC<{
  selected: GoodsOption[];
  onUpdateOption: (option: GoodsOption) => void;
  onSelectMore: () => void;
  onPressDelete: (option: GoodsOption) => void;
  onCompleteAddToCart: () => void;
  onCompletePurchase: () => void;
  onAnonymouseUserPressedPurchase: () => void;
  maxDiscountMode?: ProductOptionMaxDiscountMode;
}> = props => {
  const maxDiscountMode = props.maxDiscountMode ?? 'disabled';
  const { isAutoRedeeming } = useManageMaxDiscountCouponAutoRedeem({
    mode: maxDiscountMode,
    selected: props.selected,
  });
  const { onPressPurchase, loadingProductCheckout } = useOnPressPurchase({
    selected: props.selected,
    onCompletePurchase: props.onCompletePurchase,
    onAnonymouseUserPressedPurchase: props.onAnonymouseUserPressedPurchase,
    mode: maxDiscountMode,
    disabled: isAutoRedeeming,
  });
  const { onPressAddToCart, loadingAddToCart } = useOnPressAddToCart({
    selected: props.selected,
    onCompleteAddToCart: props.onCompleteAddToCart,
  });
  const isGiftMode = useIsBottomSheetGiftMode();
  const entered = useBottomSheetEntered();
  const { data: brief } = useGoodsOptionsBrief();
  const showMaxDiscountFreeDeliveryBadge =
    !isGiftMode &&
    maxDiscountMode !== 'disabled' &&
    (brief?.minimum_order_amount ?? 0) === 0;

  return (
    <Flex flex={1}>
      {entered ? (
        <List<GoodsOption>
          ListHeaderComponent={
            isGiftMode ? undefined : (
              <Flex>
                <PrimaryButton
                  variant={'fill'}
                  outline
                  size={'large'}
                  onPress={props.onSelectMore}
                >
                  옵션 선택하기
                </PrimaryButton>
                <VerticalSpacing size={12} />
              </Flex>
            )
          }
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingVertical: 16,
            paddingHorizontal: 16,
          }}
          data={props.selected}
          ItemSeparatorComponent={() => <VerticalSpacing size={12} />}
          renderItem={({ item }) => {
            return (
              <PricingListItem
                option={item}
                onUpdateOption={props.onUpdateOption}
                onPressDelete={() => props.onPressDelete(item)}
              />
            );
          }}
          ListFooterComponent={<ReportPriceButton />}
        />
      ) : (
        <Flex.Center flex={1}>
          <Spinner />
        </Flex.Center>
      )}
      <PricingInfo
        selected={props.selected}
        mode={maxDiscountMode}
        isAutoRedeeming={isAutoRedeeming}
      />
      {isGiftMode ? (
        <Flex.Row
          padding={{ horizontal: 12, vertical: 12 }}
          borderColor={'border_tertiary'}
          borderTopWidth={1}
          gap={8}
          position={'relative'}
        >
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              onPress={onPressPurchase}
              state={
                loadingProductCheckout || isAutoRedeeming
                  ? 'loading'
                  : undefined
              }
            >
              선물하기
            </PrimaryButton>
          </Flex>
        </Flex.Row>
      ) : (
        <Flex.Row
          padding={{ horizontal: 12, vertical: 12 }}
          borderColor={'border_tertiary'}
          borderTopWidth={1}
          gap={8}
          position={'relative'}
        >
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              outline={true}
              onPress={() => onPressAddToCart(false)}
              state={
                loadingAddToCart
                  ? 'loading'
                  : props.selected.length === 0
                    ? 'disabled'
                    : undefined
              }
            >
              장바구니
            </PrimaryButton>
          </Flex>
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              onPress={onPressPurchase}
              state={
                loadingProductCheckout || isAutoRedeeming
                  ? 'loading'
                  : props.selected.length === 0
                    ? 'disabled'
                    : undefined
              }
            >
              구매하기
            </PrimaryButton>
          </Flex>
          {showMaxDiscountFreeDeliveryBadge && (
            <Flex position={'absolute'} top={5} right={20}>
              <ImageBadgeFreeDelivery />
            </Flex>
          )}
        </Flex.Row>
      )}
    </Flex>
  );
};
